import { CircularProgress, Dialog, Tab, Tabs } from "@mui/material";
import Button from "components/common/Button";
import ModificationSelection from "components/common/ModificationSelection";
import api from "config/api";
import { useAppSelector } from "hooks/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { convertNumberToHex } from "screens";
import { ModifierItemType } from "utils/types";
import { flushSync } from "react-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type Props = {
  open: any;
  setOpen: (v: any) => void;
  onSubmit: (v: any) => void;
};

const toTitleCase = (value: any) =>
  String(value || "")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const formatToppingName = (value: any) =>
  toTitleCase(value)
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

const formatSelectedTopping = (item: any) => {
  const name = formatToppingName(
    item?.pizzaModifierName || item?.modifierName || item?.description,
  );

  if (!name) return "";
  return Number(item?.quantity || 1) === 2 ? `2x ${name}` : name;
};

const ModificationPopUp = ({ open, setOpen, onSubmit }: Props) => {
  const [loading, setLoading] = useState(false);
  const [tabs, setTabs] = useState<ModifierItemType[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [config, setConfig] = useState({});
  const [defaultToppings, setDefaulToppings] = useState<any>({});
  const [showSelectedSummary, setShowSelectedSummary] = useState(false);
  const configRef = useRef<any>({});
  const { settings } = useAppSelector(({ main }) => main);

  const updateConfig = (updater: any) => {
    setConfig((prev: any) => {
      const next =
        typeof updater === "function"
          ? updater(prev)
          : {
              ...prev,
              ...updater,
            };
      configRef.current = next;
      return next;
    });
  };

  const buildCategoryConfig = (category: any, savedModifiers: any[] = []) => {
    const savedMap = new Map(
      (savedModifiers || []).map((item: any) => [
        Number(item?.pizzaModifierId || item?.modifierId),
        item,
      ]),
    );

    return (category?.modifiers || []).map((modifier: any) => {
      const saved = savedMap.get(Number(modifier?.pizzaModifierId));
      const selected =
        typeof saved?.selected === "boolean"
          ? saved.selected
          : saved
          ? true
          : typeof modifier?.selected === "boolean"
          ? modifier.selected
          : Number(modifier?.defaultTopping) === 1;

      return {
        ...modifier,
        ...saved,
        quantity:
          Number(
            saved?.Multiplier ||
              saved?.DtltoppMultiplier ||
              saved?.legacyQuantity ||
              modifier?.Multiplier ||
              modifier?.DtltoppMultiplier ||
              modifier?.quantity ||
              1,
          ) === 2
            ? 2
            : 1,
        pizzaRegion: Number(
          saved?.pizzaRegion ?? saved?.PizzaRegion ?? modifier?.pizzaRegion ?? modifier?.PizzaRegion ?? 1,
        ),
        selected,
      };
    });
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      setShowSelectedSummary(false);
      api
        .get(`/api/Modifier/ModifierCategories?${new URLSearchParams(open)}`)
        .then(({ data }) => {
          setSelectedTab(data[0].modifierId);
          setTabs(data);
          const temp: any = {};
          const list = data.reduce((obj: any, itm: any) => {
            const savedCategory = open?.modifications?.[itm.modifierId] || [];
            temp[itm.modifierId] = itm.modifiers.filter(
              (i: any) => Number(i.defaultTopping) === 1,
            ).length;

            return {
              ...obj,
              [itm.modifierId]: buildCategoryConfig(itm, savedCategory),
            };
          }, {});

          setConfig(list);
          configRef.current = list;
          setDefaulToppings(temp);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const selectOptions = useMemo(
    () => tabs.find((i) => i.modifierId === selectedTab)?.modifiers || [],
    [tabs, selectedTab],
  );

  const selectedSummary = useMemo(() => {
    const groups = {
      whole: [] as string[],
      left: [] as string[],
      right: [] as string[],
    };

    Object.values(config || {})
      .flat()
      .filter((item: any) => item?.selected)
      .forEach((item: any) => {
        const label = formatSelectedTopping(item);
        if (!label) return;

        const region = Number(item?.pizzaRegion || 1);
        if (region === 2) {
          groups.left.push(label);
        } else if (region === 3) {
          groups.right.push(label);
        } else {
          groups.whole.push(label);
        }
      });

    return groups;
  }, [config]);

  const hasSelectedSummary = useMemo(
    () =>
      selectedSummary.whole.length > 0 ||
      selectedSummary.left.length > 0 ||
      selectedSummary.right.length > 0,
    [selectedSummary],
  );

  const selectedCount = useMemo(
    () =>
      Object.values(config || {})
        .flat()
        .filter((item: any) => item?.selected)
        .reduce(
          (sum: number, item: any) =>
            sum + (Number(item?.quantity || 1) === 2 ? 2 : 1),
          0,
        ),
    [config],
  );

  return (
    <Dialog
      onClose={() => setOpen(undefined)}
      open={!!open}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
      }}
      PaperProps={{
        className: "rounded-[24px] overflow-hidden",
        sx: {
          mt: { xs: 2, sm: "45px" },
          mx: { xs: 1, sm: 2 },
          mb: { xs: "calc(env(safe-area-inset-bottom) + 88px)", sm: 2 },
          height: {
            xs: "calc(100vh - env(safe-area-inset-bottom) - 104px)",
            sm: "auto",
          },
          maxHeight: {
            xs: "calc(100vh - env(safe-area-inset-bottom) - 104px)",
            sm: "calc(100vh - 61px)",
          },
          "@supports (height: 100dvh)": {
            height: {
              xs: "calc(100dvh - env(safe-area-inset-bottom) - 104px)",
              sm: "auto",
            },
            maxHeight: {
              xs: "calc(100dvh - env(safe-area-inset-bottom) - 104px)",
              sm: "calc(100vh - 61px)",
            },
          },
        },
      }}
    >
      <div className="flex h-full min-h-0 min-w-[80vw] flex-col bg-[#fcfcfa] sm:max-h-[92vh] sm:min-h-[70vh] md:min-w-[600px]">
        <div className="shrink-0 border-b border-black/10 px-4 pt-5 pb-4 sm:px-5">
          <h4 className="!text-lg text-[#222] font-bold">
            Customize Your Order
          </h4>
          <p className="mt-1 text-[13px] text-gray-500">
            Pick the extras you want before saving.
          </p>
        </div>
        {!!tabs.length && (
          <div className="shrink-0 px-2 sm:px-3 border-b border-black/10 bg-white">
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              aria-label="basic tabs example"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 50,
                "& .MuiTab-root": {
                  minHeight: 50,
                  textTransform: "none",
                  fontWeight: 600,
                },
                "& .Mui-selected": {
                  color: `${convertNumberToHex(
                    settings.themeColor,
                  )} !important`,
                },
              }}
            >
              {tabs.map((i) => (
                <Tab value={i.modifierId} label={i.modifierName} />
              ))}
            </Tabs>
          </div>
        )}

        <div className="min-h-0 flex flex-1 flex-col">
          {loading ? (
            <CircularProgress className="self-center mt-[10%]" />
          ) : (
            <ModificationSelection
              label="pizzaModifierName"
              selectedTab={selectedTab}
              config={config}
              setConfig={updateConfig}
              options={selectOptions}
            />
          )}
        </div>
        <div className="relative shrink-0 border-t border-black/10 bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-6 sm:pb-4">
          <button
            type="button"
            aria-label={showSelectedSummary ? "Hide selected toppings" : "Show selected toppings"}
            title={showSelectedSummary ? "Hide selected toppings" : "Show selected toppings"}
            onClick={() => setShowSelectedSummary((value) => !value)}
            className="absolute left-1/2 top-0 flex h-8 min-w-[112px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1 rounded-full border border-[#ece7dc] bg-[#faf8f2] px-3 text-[11px] font-bold text-[#3f3a33] shadow-sm"
          >
            {showSelectedSummary ? (
              <KeyboardArrowDownIcon className="!text-[18px]" />
            ) : (
              <KeyboardArrowUpIcon className="!text-[18px]" />
            )}
            Selected {selectedCount}
          </button>

          {showSelectedSummary && (
            <div className="mb-3 min-w-0 rounded-xl border border-[#ece7dc] bg-[#faf8f2] px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#8a8173]">
                Selected
              </div>
              <div className="mt-1 max-h-[112px] space-y-1 overflow-y-auto text-[12px] leading-5 text-[#2d2a25] break-words">
                {hasSelectedSummary ? (
                  <>
                    {!!selectedSummary.whole.length && (
                      <div>
                        <span className="font-semibold">Whole:</span>{" "}
                        <span>{selectedSummary.whole.join(", ")}</span>
                      </div>
                    )}
                    {!!selectedSummary.left.length && (
                      <div>
                        <span className="font-semibold">Left Half:</span>{" "}
                        <span>{selectedSummary.left.join(", ")}</span>
                      </div>
                    )}
                    {!!selectedSummary.right.length && (
                      <div>
                        <span className="font-semibold">Right Half:</span>{" "}
                        <span>{selectedSummary.right.join(", ")}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <span>No toppings selected</span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <Button
              className="!rounded-[16px] !px-5"
              onClick={() => setOpen(undefined)}
            >
              Cancel
            </Button>
            <Button
              className="!rounded-[16px] !px-6"
              onClick={() => {
              flushSync(() => {});
              const latestConfig = configRef.current;
              onSubmit(
                Object.entries(latestConfig).reduce((obj: any, [key, arr]: any) => {
                  const array = structuredClone(arr || []);
                  const selectedItems = array.filter((value: any) => value.selected);
                  const freeLimit = Number(defaultToppings[key] || 0);
                  const toChargeableUnits = (items: any[] = []) =>
                    items
                      .filter((value: any) => Number(value.pizzaModifierPrice || 0) > 0)
                      .flatMap((value: any) =>
                        Array.from(
                          { length: Number(value?.quantity || 1) === 2 ? 2 : 1 },
                          () => value,
                        ),
                      );
                  const sortChargeableUnits = (items: any[] = []) =>
                    [...items].sort(
                      (a: any, b: any) =>
                        Number(a.pizzaModifierPrice || 0) -
                          Number(b.pizzaModifierPrice || 0) ||
                        Number(a.pizzaModifierId || 0) -
                          Number(b.pizzaModifierId || 0),
                    );
                  const defaultUnits = toChargeableUnits(
                    selectedItems.filter(
                      (value: any) => Number(value?.defaultTopping || 0) === 1,
                    ),
                  );
                  const extraUnits = sortChargeableUnits(
                    toChargeableUnits(
                      selectedItems.filter(
                        (value: any) => Number(value?.defaultTopping || 0) !== 1,
                      ),
                    ),
                  );
                  const extraFreeLimit = Math.max(0, freeLimit - defaultUnits.length);
                  const freeCounts = [
                    ...defaultUnits.slice(0, freeLimit),
                    ...extraUnits.slice(0, extraFreeLimit),
                  ].reduce((map: Map<number, number>, value: any) => {
                    const modifierId = Number(value.pizzaModifierId);
                    map.set(modifierId, (map.get(modifierId) || 0) + 1);
                    return map;
                  }, new Map<number, number>());

                  const chargedCounts = [
                    ...defaultUnits.slice(freeLimit),
                    ...extraUnits.slice(extraFreeLimit),
                  ].reduce((map: Map<number, number>, value: any) => {
                      const modifierId = Number(value.pizzaModifierId);
                      map.set(modifierId, (map.get(modifierId) || 0) + 1);
                      return map;
                    }, new Map<number, number>());

                  obj[key] = array.map((value: any) => {
                    const price = Number(value?.pizzaModifierPrice || 0);
                    const isSelected = !!value?.selected;
                    const modifierId = Number(value.pizzaModifierId);
                    const quantity = Number(value?.quantity || 1) === 2 ? 2 : 1;
                    const pizzaRegion = Number(value?.pizzaRegion ?? value?.PizzaRegion ?? 1);
                    const freeQuantity = freeCounts.get(modifierId) || 0;
                    const chargedQuantity = chargedCounts.get(modifierId) || 0;
                    const getsFreeSlot = isSelected && price > 0 && freeQuantity > 0;
                    const sideFactor = [2, 3].includes(pizzaRegion)
                      ? 0.5
                      : 1;

                    if (!value.defaultTopping && getsFreeSlot) {
                      value.newDefaultTopping = 1;
                    } else {
                      delete value.newDefaultTopping;
                    }
                    value.quantity = quantity;
                    value.isMultiplier = quantity > 1 ? 1 : 0;
                    value.Multiplier = quantity;
                    value.multiplier = quantity;
                    value.pizzaRegion = pizzaRegion;
                    value.PizzaRegion = pizzaRegion;
                    value.subTotal = +(price * sideFactor * chargedQuantity).toFixed(2);
                    value.totalValue = value.subTotal;
                    return value;
                  });
                  return obj;
                }, {}),
              );
              }}
            >
              Apply Toppings
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ModificationPopUp;
